/**
 * Beatly API (api-v2) service.
 * Single caller module for all beatly-api-v2 requests; retry and base URL in one place.
 */

import { withRetry } from '@/utils/withRetry';
import type { AuthUser, AuthAccountManager } from '@/utils/auth';
import { beatlyApiErrorFromParsedBody, getBeatlyApiBaseUrl } from '@/api/beatlyApiBase';

export interface BeatlyApiError extends Error {
  status?: number;
  code?: string;
}

function createApiError(message: string, status?: number, code?: string): BeatlyApiError {
  const err = new Error(message) as BeatlyApiError;
  err.status = status;
  err.code = code;
  return err;
}

async function post<T>(path: string, body: object, token?: string): Promise<T> {
  const base = getBeatlyApiBaseUrl();
  const url = `${base.replace(/\/$/, '')}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return withRetry(async () => {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const { message, code } = beatlyApiErrorFromParsedBody(
        data,
        response.status,
        `Request failed: ${response.status}`
      );
      throw createApiError(message, response.status, code);
    }

    return data as T;
  });
}

async function put<T>(path: string, body: object, token: string): Promise<T> {
  const base = getBeatlyApiBaseUrl();
  const url = `${base.replace(/\/$/, '')}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return withRetry(async () => {
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    });

    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const { message, code } = beatlyApiErrorFromParsedBody(
        data,
        response.status,
        `Request failed: ${response.status}`
      );
      throw createApiError(message, response.status, code);
    }

    return data as T;
  });
}

async function patch<T>(path: string, body: object, token: string): Promise<T> {
  const base = getBeatlyApiBaseUrl();
  const url = `${base.replace(/\/$/, '')}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return withRetry(async () => {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });

    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const { message, code } = beatlyApiErrorFromParsedBody(
        data,
        response.status,
        `Request failed: ${response.status}`
      );
      throw createApiError(message, response.status, code);
    }

    return data as T;
  });
}

async function del<T>(path: string, token: string): Promise<T> {
  const base = getBeatlyApiBaseUrl();
  const url = `${base.replace(/\/$/, '')}${path}`;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return withRetry(async () => {
    const response = await fetch(url, { method: 'DELETE', headers });

    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const { message, code } = beatlyApiErrorFromParsedBody(
        data,
        response.status,
        `Request failed: ${response.status}`
      );
      throw createApiError(message, response.status, code);
    }

    return data as T;
  });
}

async function get<T>(path: string, token: string): Promise<T> {
  const base = getBeatlyApiBaseUrl();
  const url = `${base.replace(/\/$/, '')}${path}`;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return withRetry(async () => {
    const response = await fetch(url, { method: 'GET', headers });

    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const { message, code } = beatlyApiErrorFromParsedBody(
        data,
        response.status,
        `Request failed: ${response.status}`
      );
      throw createApiError(message, response.status, code);
    }

    return data as T;
  });
}

async function postFormData<T>(path: string, formData: FormData, token: string): Promise<T> {
  const base = getBeatlyApiBaseUrl();
  const url = `${base.replace(/\/$/, '')}${path}`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  return withRetry(async () => {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    let data: unknown;
    try {
      const text = await response.text();
      data = text ? (JSON.parse(text) as unknown) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const { message, code } = beatlyApiErrorFromParsedBody(
        data,
        response.status,
        `Request failed: ${response.status}`
      );
      throw createApiError(message, response.status, code);
    }

    return data as T;
  });
}

type AppS3UploadScope = 'contenthub-collection-picture';

interface AppS3UploadCreateRequest {
  filename: string;
  content_type: string;
  scope: AppS3UploadScope;
}

interface AppS3UploadSession {
  upload_url?: string;
  upload_token?: string;
}

interface AppS3UploadCompleteResponse {
  upload_token?: string;
  filename?: string;
  resource_type?: 'video' | 'image' | 'raw';
}

function getFileContentType(file: File): string {
  return file.type || 'application/octet-stream';
}

async function createAppS3Upload(token: string, body: AppS3UploadCreateRequest): Promise<AppS3UploadSession> {
  return post<AppS3UploadSession>('/app/v2/uploads/s3/create', body, token);
}

async function completeAppS3Upload(token: string, uploadToken: string): Promise<AppS3UploadCompleteResponse> {
  return post<AppS3UploadCompleteResponse>('/app/v2/uploads/s3/complete', { upload_token: uploadToken }, token);
}

async function putFileToAppS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': getFileContentType(file),
    },
    body: file,
  });
  if (!response.ok) {
    throw createApiError(`Failed to upload file to S3: ${response.status}`, response.status);
  }
}

async function uploadFileViaDirectS3(
  token: string,
  file: File,
  scope: AppS3UploadScope
): Promise<{ uploadToken: string; completed: AppS3UploadCompleteResponse }> {
  const upload = await createAppS3Upload(token, {
    filename: file.name,
    content_type: getFileContentType(file),
    scope,
  });
  if (!upload.upload_url || !upload.upload_token) {
    throw createApiError('Upload session did not return an S3 upload URL', 500, 'INVALID_RESPONSE');
  }
  await putFileToAppS3(upload.upload_url, file);
  const completed = await completeAppS3Upload(token, upload.upload_token);
  return { uploadToken: upload.upload_token, completed };
}

export interface SendMagicLinkResponse {
  code: 'SENT_LOGIN_MAGICLINK' | 'SENT_REGISTER_MAGICLINK';
  token?: string;
}

/** Matches api-v2 `v2MagicLinkRequest.account_type` for new-user registration. */
export type SignupAccountType = 'advertiser' | 'creator';

export interface SendMagicLinkBody {
  email: string;
  first_name?: string;
  account_type?: SignupAccountType;
  target?: string;
}

export interface SignupMagicLinkBody {
  email: string;
  first_name: string;
  account_type: SignupAccountType;
}

/**
 * Body for POST /app/v2/authenticate/magiclink/send-email when registering a new user.
 * api-v2 requires `account_type` and `first_name` when the email is not yet registered.
 */
export function buildSignupMagicLinkBody(
  email: string,
  first_name: string,
  account_type: SignupAccountType,
): SignupMagicLinkBody {
  return {
    email: email.trim(),
    first_name: first_name.trim(),
    account_type,
  };
}

export async function sendMagicLinkEmail(body: SendMagicLinkBody): Promise<SendMagicLinkResponse> {
  return post<SendMagicLinkResponse>('/app/v2/authenticate/magiclink/send-email', body);
}

/** Signup-only magic link: always sends advertiser or creator per step-1 role choice. */
export async function sendSignupMagicLinkEmail(body: SignupMagicLinkBody): Promise<SendMagicLinkResponse> {
  return sendMagicLinkEmail(body);
}

export interface AuthenticateWithTokenResponse {
  token: string;
  user: unknown;
  code?: string;
}

export async function authenticateWithToken(token: string): Promise<AuthenticateWithTokenResponse> {
  return post<AuthenticateWithTokenResponse>('/app/v2/authenticate/with-token', { token });
}

/**
 * Fetch current user profile from api-v2. Validates the token; any valid user (client or influencer) is accepted.
 */
export async function getCurrentUser(token: string): Promise<AuthUser> {
  return get<AuthUser>('/app/v2/user', token);
}


/** One team row from GET /app/v2/teams/search (subset of API fields used in UI). */
export interface ClientTeamSearchRow {
  team_id: string;
  name: string;
  role?: string;
  date_created?: string;
  date_joined?: string;
  content_hub_url?: string;
  sentiment_app_url?: string;
  has_mention_enabled?: boolean;
  has_ad_accounts_enabled?: boolean;
  account_manager?: AuthAccountManager;
  billing?: {
    company_name?: string;
    vat_number?: string;
    address?: Record<string, unknown>;
  };
}

export interface TeamsSearchResponse {
  teams: ClientTeamSearchRow[];
}

/**
 * List teams the authenticated user belongs to (newest membership first).
 * GET /app/v2/teams/search
 */
export async function getTeamsSearch(token: string): Promise<TeamsSearchResponse> {
  return get<TeamsSearchResponse>('/app/v2/teams/search', token);
}

export interface UpdateTeamBody {
  name?: string | null;
}

/**
 * Update a team (name, billing, etc.). We currently use it for renaming.
 * PATCH /app/v2/team/:teamId/update
 */
export async function updateTeam(teamId: string, token: string, body: UpdateTeamBody): Promise<ClientTeamSearchRow> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/update`;
  return patch<ClientTeamSearchRow>(path, body, token);
}

// Matches api-v2 OpenAPI enum: TeamMemberUpdateRequest.role / v2TeamMember.role
export type TeamMemberRole = 'ADMIN' | 'MARKETING_MANAGER' | 'EXTERNAL_PARTNER';

/** Per-brand notification and visibility settings (v2BrandSettings). */
export interface TeamMemberBrandSettingRow {
  brand_id: string;
  name?: string | null;
  want_email_notifications: boolean;
  show_campaigns: boolean;
}

export interface TeamMemberRow {
  user_id: string;
  name: string;
  email: string;
  role: TeamMemberRole;
  status?: string;
  date_invited?: string;
  mugshot_url?: string;
  avatar_url?: string;
  picture_url?: string;
  brand_settings?: TeamMemberBrandSettingRow[];
  /** Team-default: receives curation review emails when true/omitted. */
  is_curation_reviewer?: boolean;
  /** Team-default: receives draft review emails when true/omitted. */
  is_draft_reviewer?: boolean;
  [key: string]: unknown;
}

export interface TeamMembersSearchResponse {
  members: TeamMemberRow[];
}

/**
 * List team members for the given team.
 * GET /app/v2/team/:teamId/members/search
 */
export async function getTeamMembersSearch(teamId: string, token: string): Promise<TeamMembersSearchResponse> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/members/search`;
  return get<TeamMembersSearchResponse>(path, token);
}

export interface TeamBrandRow {
  brand_id: string;
  name: string;
  logo?: string;
  logo_url?: string;
  logo_square_url?: string;
  url?: string;
  domain?: string;
  description?: string;
  [key: string]: unknown;
}

export interface TeamBrandsSearchResponse {
  brands: TeamBrandRow[];
}

/**
 * List brands attached to the given team.
 * GET /app/v2/team/:teamId/brands/search
 */
export async function getTeamBrandsSearch(teamId: string, token: string): Promise<TeamBrandsSearchResponse> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/brands/search`;
  return get<TeamBrandsSearchResponse>(path, token);
}

export interface TeamBrandCreateBody {
  name: string;
  url?: string;
  logo_url?: string;
}

/**
 * Upload a logo image file for the given brand.
 * POST /app/v2/team/:teamId/brand/:brandId/upload-logo
 */
export async function uploadTeamBrandLogo(teamId: string, brandId: string, token: string, file: File): Promise<TeamBrandRow> {
  const formData = new FormData();
  formData.append('file', file);
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/brand/${encodeURIComponent(brandId)}/upload-logo`;
  return postFormData<TeamBrandRow>(path, formData, token);
}

export interface TeamBrandUpdateBody {
  name?: string | null;
  description?: string | null;
  url?: string | null;
  logo_url?: string | null;
  logo_cloudinary_id?: string | null;
  logo_s3_upload_token?: string | null;
}

/**
 * Add a brand to the team (creates or attaches).
 * PUT /app/v2/team/:teamId/brand/create
 */
export async function createTeamBrand(teamId: string, token: string, body: TeamBrandCreateBody): Promise<TeamBrandRow> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/brand/create`;
  return put<TeamBrandRow>(path, body, token);
}

/**
 * Update a team brand.
 * PATCH /app/v2/team/:teamId/brand/:brandId/update
 */
export async function updateTeamBrand(
  teamId: string,
  brandId: string,
  token: string,
  body: TeamBrandUpdateBody
): Promise<TeamBrandRow> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/brand/${encodeURIComponent(brandId)}/update`;
  return patch<TeamBrandRow>(path, body, token);
}

/**
 * Archive a brand for the given team.
 * POST /app/v2/team/:teamId/brand/:brandId/archive
 */
export async function archiveTeamBrand(teamId: string, brandId: string, token: string): Promise<TeamBrandRow> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/brand/${encodeURIComponent(brandId)}/archive`;
  return post<TeamBrandRow>(path, { team_id: teamId, brand_id: brandId }, token);
}

export interface InviteTeamMemberBody {
  name: string;
  email: string;
  role: TeamMemberRole;
}

/**
 * Invite a team member.
 * POST /app/v2/team/:teamId/member/invite
 */
export async function inviteTeamMember(teamId: string, token: string, body: InviteTeamMemberBody): Promise<TeamMemberRow> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/member/invite`;
  return post<TeamMemberRow>(path, body, token);
}

/** Partial team-member update: role and/or team-default reviewer flags. */
export interface UpdateTeamMemberBody {
  role?: TeamMemberRole;
  is_curation_reviewer?: boolean;
  is_draft_reviewer?: boolean;
}

/**
 * Update a team member role and/or team-default reviewer flags.
 * PATCH /app/v2/team/:teamId/member/:userId/update
 */
export async function updateTeamMember(teamId: string, userId: string, token: string, body: UpdateTeamMemberBody): Promise<TeamMemberRow> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/member/${encodeURIComponent(userId)}/update`;
  return patch<TeamMemberRow>(path, body, token);
}

export interface UpdateTeamMemberBrandSettingsBody {
  want_email_notifications?: boolean;
  show_campaigns?: boolean;
}

/**
 * Update per-brand email / campaign visibility for a team member.
 * PATCH /app/v2/team/:teamId/member/:userId/brand/:brandId/update
 */
export async function updateTeamMemberBrandSettings(
  teamId: string,
  userId: string,
  brandId: string,
  token: string,
  body: UpdateTeamMemberBrandSettingsBody
): Promise<TeamMemberRow> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/member/${encodeURIComponent(userId)}/brand/${encodeURIComponent(brandId)}/update`;
  return patch<TeamMemberRow>(path, body, token);
}

/**
 * Remove a team member from the team (or leave the team if userId is self).
 * DELETE /app/v2/team/:teamId/member/:userId/remove
 */
export async function removeTeamMember(teamId: string, userId: string, token: string): Promise<Record<string, unknown>> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/member/${encodeURIComponent(userId)}/remove`;
  return del<Record<string, unknown>>(path, token);
}

export interface SelectClientTeamResponse {
  token: string;
  user: AuthUser;
}

/**
 * Set the client's current team. Returns a fresh JWT and the updated user profile (same shape as GET /app/v2/user).
 * POST /app/v2/team/:teamId/select
 */
export async function selectClientTeam(teamId: string, token: string): Promise<SelectClientTeamResponse> {
  const path = `/app/v2/team/${encodeURIComponent(teamId)}/select`;
  return post<SelectClientTeamResponse>(path, {}, token);
}

export type UpdateUserBody = {
  want_email_notifications?: boolean;
  want_sms_notifications?: boolean;
  send_phone_verification_sms?: boolean;
  language?: string;
  first_name?: string;
  last_name?: string;
  country?: string;
  campaign_countries?: string[];
  email?: string;
  phone?: string;
  tax_country?: string;
  shipping_address?: unknown;
};

export type ApiV2CodeResponse = {
  code?: string;
  isExpired?: boolean;
  [key: string]: unknown;
};

export type AuthUserOrApiV2Code = AuthUser | ApiV2CodeResponse;

export async function updateCurrentUser(token: string, body: UpdateUserBody): Promise<AuthUserOrApiV2Code> {
  return patch<AuthUserOrApiV2Code>('/app/v2/user/update', body, token);
}

export type EmailRequestBody = {
  email: string;
};

export type PhoneVerificationRequestBody = {
  otp: string;
};

export async function verifyUserPhone(
  token: string,
  body: PhoneVerificationRequestBody
): Promise<AuthUserOrApiV2Code> {
  return post<AuthUserOrApiV2Code>('/app/v2/user/verify/phone', body, token);
}

export async function addUserEmail(
  token: string,
  body: EmailRequestBody
): Promise<AuthUserOrApiV2Code> {
  return post<AuthUserOrApiV2Code>('/app/v2/user/email/add', body, token);
}

export async function verifyUserEmail(
  token: string,
  body: EmailRequestBody
): Promise<AuthUserOrApiV2Code> {
  return post<AuthUserOrApiV2Code>('/app/v2/user/email/verify', body, token);
}

export async function forgetUserEmail(
  token: string,
  body: EmailRequestBody
): Promise<AuthUserOrApiV2Code> {
  return post<AuthUserOrApiV2Code>('/app/v2/user/email/forget', body, token);
}

export async function makePrimaryUserEmail(
  token: string,
  body: EmailRequestBody
): Promise<AuthUserOrApiV2Code> {
  return post<AuthUserOrApiV2Code>('/app/v2/user/email/make-primary', body, token);
}

// ---------------------------------------------------------------------------
// Content Hub (api-v2)
// ---------------------------------------------------------------------------

export interface ContentHubPathRow {
  team_id: string;
  team_name: string;
  brand_id: string;
  brand_name: string;
  brand_logo_url: string;
  campaign_id: string;
  campaign_name: string;
  /** Calendar year of campaign start from API; omitted when unknown. */
  campaign_year?: number | null;
  /** ISO 3166-1 alpha-2 audience country codes (sorted, uppercase) from API. */
  campaign_countries?: string[];
  collaboration_id: string;
  influencer_name: string;
  influencer_handle: string;
  influencer_mugshot_url?: string;
}

export interface ContentHubPathsResponse {
  folders?: ContentHubPathRow[];
  code?: string;
  isExpired?: boolean;
}

export async function getContentHubPaths(token: string): Promise<ContentHubPathsResponse> {
  return get<ContentHubPathsResponse>('/app/v2/contenthub/paths', token);
}

export interface ContentHubMedia {
  media_id: string;
  contenthub_url: string;
  preview_url?: string;
  filename?: string;
  date_created?: string;
  campaign_name?: string;
  influencer_name?: string;
  influencer_handle?: string;
  product_type?: string;
  beat_name?: string;
  team_id?: string;
  brand_id?: string;
  campaign_id?: string;
  collaboration_id?: string;
  is_raw?: boolean;
  /** BO (cross-team) search only: display names for the owning team and brand. */
  team_name?: string;
  brand_name?: string;
  [key: string]: unknown;
}

export type ContentHubKind = 'all' | 'raw' | 'final';

export interface ContentHubMediaPage {
  medias?: ContentHubMedia[];
  page_number?: number;
  page_size?: number;
  count_total?: number;
  has_more?: boolean;
  code?: string;
  isExpired?: boolean;
  [key: string]: unknown;
}

const LEGACY_CONTENT_HUB_URL_PREFIX = 'https://a.beatly.com';

export function isLegacyContentHubMedia(media: Pick<ContentHubMedia, 'contenthub_url' | 'preview_url'>): boolean {
  const urls = [media.contenthub_url, media.preview_url];
  return urls.some((url) => {
    if (typeof url !== 'string') return false;
    return url.trim().toLowerCase().startsWith(LEGACY_CONTENT_HUB_URL_PREFIX);
  });
}

export function filterLegacyContentHubMediaPage(page: ContentHubMediaPage): ContentHubMediaPage {
  if (!Array.isArray(page.medias)) return page;
  const medias = page.medias.filter((media) => !isLegacyContentHubMedia(media));
  if (medias.length === page.medias.length) return page;
  return { ...page, medias };
}

export type EmptyResponse = {
  [key: string]: unknown;
};

export type GetContentHubSearchParams = {
  campaignId?: string | null;
  collaborationId?: string | null;
  collectionId?: string | null;
  pageNumber?: number;
  pageSize?: number;
  text?: string | null;
  kind?: ContentHubKind;
};

export async function getContentHubSearch(
  token: string,
  { campaignId = null, collaborationId = null, collectionId = null, pageNumber = 0, pageSize = 24, text = null, kind = 'all' }: GetContentHubSearchParams
): Promise<ContentHubMediaPage> {
  const params = new URLSearchParams();
  if (campaignId) params.set('campaign_id', campaignId);
  if (collaborationId) params.set('collaboration_id', collaborationId);
  if (collectionId) params.set('collection_id', collectionId);
  params.set('page_number', String(pageNumber));
  params.set('page_size', String(pageSize));
  if (text) params.set('text', text);
  if (kind && kind !== 'all') params.set('kind', kind);
  const page = await get<ContentHubMediaPage>(`/app/v2/contenthub/search?${params.toString()}`, token);
  return filterLegacyContentHubMediaPage(page);
}

export type GetContentHubFavoritesSearchParams = {
  campaignId?: string | null;
  collaborationId?: string | null;
  pageNumber?: number;
  pageSize?: number;
  text?: string | null;
};

export async function getContentHubFavoritesSearch(
  token: string,
  { campaignId = null, collaborationId = null, pageNumber = 0, pageSize = 24, text = null }: GetContentHubFavoritesSearchParams
): Promise<ContentHubMediaPage> {
  const params = new URLSearchParams();
  if (campaignId) params.set('campaign_id', campaignId);
  if (collaborationId) params.set('collaboration_id', collaborationId);
  params.set('page_number', String(pageNumber));
  params.set('page_size', String(pageSize));
  if (text) params.set('text', text);
  const page = await get<ContentHubMediaPage>(`/app/v2/contenthub/favorites/search?${params.toString()}`, token);
  return filterLegacyContentHubMediaPage(page);
}

export async function likeContentHubMedia(token: string, mediaId: string): Promise<EmptyResponse> {
  return post<EmptyResponse>(`/app/v2/contenthub/media/${mediaId}/like`, {}, token);
}

export async function unlikeContentHubMedia(token: string, mediaId: string): Promise<EmptyResponse> {
  return post<EmptyResponse>(`/app/v2/contenthub/media/${mediaId}/unlike`, {}, token);
}

export type ContentHubCollectionVisibility = 'PRIVATE' | 'TEAM';

export interface ContentHubCollection {
  collection_id: string;
  name: string;
  description?: string | null;
  picture_url?: string | null;
  visibility?: ContentHubCollectionVisibility | string;
  content_count?: number;
  // Backward/forward compatible aliases (depending on api-v2 version)
  media_count?: number;
  item_count?: number;
  date_created: string;
  date_updated: string;
}

export interface ContentHubCollectionsPage {
  page_number: number;
  page_size: number;
  has_more: boolean;
  collections: ContentHubCollection[];
  code?: string;
  isExpired?: boolean;
}

export async function getContentHubCollectionsSearch(
  token: string,
  params: { pageNumber?: number; pageSize?: number; scope?: 'private' | 'team' } = {}
): Promise<ContentHubCollectionsPage> {
  const pageNumber = params.pageNumber ?? 0;
  const pageSize = params.pageSize ?? 20;
  const scope = params.scope;
  const qs = new URLSearchParams();
  qs.set('page_number', String(pageNumber));
  qs.set('page_size', String(pageSize));
  if (scope) qs.set('scope', scope);
  return get<ContentHubCollectionsPage>(`/app/v2/contenthub/collections/search?${qs.toString()}`, token);
}

export async function createContentHubCollection(
  token: string,
  body: { name: string; description?: string | null; visibility?: ContentHubCollectionVisibility }
): Promise<ContentHubCollection> {
  return post<ContentHubCollection>('/app/v2/contenthub/collection/create', body, token);
}

export async function updateContentHubCollection(
  token: string,
  collectionId: string,
  body: { name?: string | null; description?: string | null; picture_s3_upload_token?: string | null }
): Promise<ContentHubCollection> {
  return patch<ContentHubCollection>(`/app/v2/contenthub/collection/${collectionId}/update`, body, token);
}

export async function addContentHubCollectionMedias(
  token: string,
  collectionId: string,
  mediaIds: string[]
): Promise<ContentHubCollection> {
  return post<ContentHubCollection>(
    `/app/v2/contenthub/collection/${collectionId}/add-medias`,
    { media_ids: mediaIds },
    token
  );
}

export async function removeContentHubCollectionMedias(
  token: string,
  collectionId: string,
  mediaIds: string[]
): Promise<ContentHubCollection> {
  return post<ContentHubCollection>(
    `/app/v2/contenthub/collection/${collectionId}/remove-medias`,
    { media_ids: mediaIds },
    token
  );
}

export async function archiveContentHubCollection(token: string, collectionId: string): Promise<ContentHubCollection> {
  return del<ContentHubCollection>(`/app/v2/contenthub/collection/${collectionId}/archive`, token);
}

export async function uploadContentHubCollectionPicture(
  token: string,
  collectionId: string,
  file: File
): Promise<ContentHubCollection> {
  const { uploadToken } = await uploadFileViaDirectS3(token, file, 'contenthub-collection-picture');
  return updateContentHubCollection(
    token,
    collectionId,
    { picture_s3_upload_token: uploadToken }
  );
}

// ─── Client campaigns (same contract as beatly-web-app) ─────────────────────

export interface ClientCampaignBudget {
  amount: number;
  currency: string;
}

/** Phase/status rows from GET campaign (same as api-v2 `todos`). */
export interface ClientCampaignTodoItem {
  name: string;
  status: 'PLANNED' | 'ONGOING' | 'DONE';
}

/** Brand sub-object on a campaign (matches api-v2 `v2Brand`). */
export interface ClientBrand {
  brand_id: string;
  name: string;
  description?: string;
  url?: string;
  date_created: string;
  has_logo: boolean;
  logo_square_url: string;
  is_archived: boolean;
}

/** Campaign list item or full detail from GET .../campaigns/search or GET .../campaign/:id (matches Api.Client.Campaign from web-app). */
export interface ClientCampaign {
  campaign_id: string;
  name: string;
  description?: string;
  date_start: string;
  /** Stored campaign start (api-v2 `date_start_real`); same source as BO campaign `date_start`. */
  date_start_real?: string | null;
  date_end: string;
  date_created?: string;
  date_updated?: string;
  status: string;
  platforms: string[];
  is_canceled?: boolean;
  budget: ClientCampaignBudget;
  /** Nested brand object (returned by GET .../campaign/:id). */
  brand?: ClientBrand;
  /** @deprecated Flat brand name on the search/list endpoint. Prefer `brand.name` from GET .../campaign/:id. */
  brand_name?: string;
  /** @deprecated Flat brand logo on the search/list endpoint. Prefer `brand.logo_square_url` from GET .../campaign/:id. */
  brand_logo_url?: string;
  is_demo?: boolean;
  total_influencer_count?: number;
  beats?: ApiBeat[];
  /** Optional report/results URL (e.g. link to analytics or report view). */
  report_url?: string;
  /** When set, embed or link to sentiment / Looker companion (api-v2 `sentiment_app_url`). */
  sentiment_app_url?: string;
  /** Whether summary tab should be offered to clients (api-v2 `has_summary`). */
  has_summary?: boolean;
  /** Outreach / content / publishing / metrics phases (api-v2 `todos`). */
  todos?: ClientCampaignTodoItem[];
  [key: string]: unknown;
}

/** Beat from campaign detail (GET .../campaign/:id). Matches Api.Beat from web-app. */
export interface SurveyTargetGroup {
  participants_total?: number | null;
  participants_pretest?: number | null;
  language?: string | null;
  country?: string | null;
  age_min?: string | number | null;
  age_max?: string | number | null;
  gender?: 'M' | 'F' | 'MF' | string | null;
}

export interface ApiBeat {
  beat_id: string;
  beat_name: string;
  /** Some client payloads use `type` (e.g. api-v2 campaign GET); older shapes use `beat_type`. */
  beat_type?: string;
  type?: string;
  platform?: string;
  date_start?: string;
  date_end?: string;
  status?: string;
  survey_url?: string | null;
  date_pretest_completed?: string | null;
  date_control_completed?: string | null;
  date_completed?: string | null;
  target_group?: SurveyTargetGroup | null;
  [key: string]: unknown;
}

/** Beat kind from campaign-detail beats: prefers `beat_type`, falls back to `type`. */
export function clientCampaignBeatType(b: Pick<ApiBeat, 'beat_type' | 'type'>): string | undefined {
  const legacy = typeof b.beat_type === 'string' ? b.beat_type.trim() : '';
  if (legacy) return legacy;
  const t = typeof b.type === 'string' ? b.type.trim() : '';
  return t || undefined;
}

/** Brief as returned by API (description + optional files). */
export interface ClientBrief {
  description?: string | null;
  files?: Array<{ filename: string; url: string }>;
  [key: string]: unknown;
}

/** Expected deliverable counts from GET .../campaign/beat/:beatId (`v2Products`). */
export interface ClientBeatProducts {
  reels?: number | null;
  stories?: number | null;
  pictures?: number | null;
  videos?: number | null;
  ads?: number | null;
  posts?: number | null;
  carousel_or_reels?: number | null;
  carousels?: number | null;
  feeds?: number | null;
  lives?: number | null;
  guides?: number | null;
  shoppings?: number | null;
  boosts?: number | null;
  short_videos?: number | null;
  long_videos?: number | null;
  [key: string]: unknown;
}

/** Beat detail with per-beat brief (GET .../campaign/beat/:beatId). */
export interface ClientCampaignBeat {
  beat_id: string;
  beat_name: string;
  beat_type?: string;
  platform?: string;
  date_start?: string;
  date_end?: string;
  status?: string;
  brief?: ClientBrief | null;
  campaign_brief?: ClientBrief | null;
  /** IGC / IGCP — how influencers receive product (`SHIPPING`, `DIGITAL`; `NONE` often omitted). */
  product_delivery?: 'SHIPPING' | 'DIGITAL' | 'NONE' | string | null;
  products?: ClientBeatProducts | null;
  survey_url?: string | null;
  date_pretest_completed?: string | null;
  date_control_completed?: string | null;
  date_completed?: string | null;
  target_group?: SurveyTargetGroup | null;
  [key: string]: unknown;
}

/** Ordered keys for brief UI parity with legacy web (same order as api-v2 product aggregation). */
export const CLIENT_BEAT_PRODUCT_KEYS = [
  'reels',
  'stories',
  'carousel_or_reels',
  'carousels',
  'feeds',
  'posts',
  'pictures',
  'videos',
  'short_videos',
  'long_videos',
  'lives',
  'guides',
  'shoppings',
  'ads',
  'boosts',
] as const;

export function listClientBeatProductEntries(
  products: ClientBeatProducts | null | undefined
): Array<{ key: string; count: number }> {
  if (!products || typeof products !== 'object') return [];
  const out: Array<{ key: string; count: number }> = [];
  const seen = new Set<string>();
  for (const k of CLIENT_BEAT_PRODUCT_KEYS) {
    const raw = products[k as keyof ClientBeatProducts];
    const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
    if (n > 0) {
      out.push({ key: k, count: n });
      seen.add(k);
    }
  }
  for (const [k, raw] of Object.entries(products)) {
    if (seen.has(k) || k === 'impressions' || k === 'clicks') continue;
    const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
    if (n > 0) out.push({ key: k, count: n });
  }
  return out;
}

/**
 * Fetch a single beat by id (includes beat-specific brief and campaign brief).
 * GET /app/v2/client/campaign/beat/:beatId
 */
export async function getCampaignBeat(
  beatId: string,
  token: string
): Promise<ClientCampaignBeat> {
  const path = `/app/v2/client/campaign/beat/${beatId}`;
  return get<ClientCampaignBeat>(path, token);
}

export interface ClientCampaignsSearchResponse {
  page_number: number;
  page_size: number;
  count_total: number;
  has_more: boolean;
  campaigns: ClientCampaign[];
}

/** Response from GET /app/v2/client/campaigns/countries */
export interface ClientCampaignCountriesResponse {
  countries: string[];
}

/** Omni list bucket; must match beatly-api-v2 `list_status` (client_search_ui_status). */
export type ClientCampaignListStatus = 'LAUNCHED' | 'DONE';

/** Sort; must match beatly-api-v2 `sort_by`. */
export type ClientCampaignSortBy =
  | 'newest'
  | 'oldest'
  | 'budget-high'
  | 'budget-low'
  | 'name-az'
  | 'name-za';

export type GetCampaignsSearchParams = {
  pageNumber: number;
  pageSize: number;
  /** When true, server returns campaigns across all teams the user belongs to (single query). */
  allTeams?: boolean;
  /** Optional; scope search to this team (must be a team the user belongs to). Omit when allTeams is true. */
  teamId?: string;
  text?: string;
  brandName?: string;
  brandId?: string;
  listStatus?: ClientCampaignListStatus;
  sortBy?: ClientCampaignSortBy;
  platform?: 'INSTAGRAM' | 'TIKTOK' | 'YOUTUBE';
  /** ISO 3166-1 alpha-2; matches backend `country` query param. */
  country?: string;
};

/**
 * Fetch paginated campaigns for the current client team, optionally for a specific team the user belongs to.
 * Same endpoint as beatly-web-app: /app/v2/client/campaigns/search
 */
export async function getCampaignsSearch(
  params: GetCampaignsSearchParams,
  token: string
): Promise<ClientCampaignsSearchResponse> {
  const {
    pageNumber,
    pageSize,
    allTeams,
    teamId,
    text,
    brandName,
    brandId,
    listStatus,
    sortBy = 'newest',
    platform,
    country,
  } = params;
  const q = new URLSearchParams({
    page_number: String(pageNumber),
    page_size: String(pageSize),
    sort_by: sortBy,
  });
  if (allTeams) {
    q.set('all_teams', 'true');
  } else if (teamId) {
    q.set('team_id', teamId);
  }
  const s = text?.trim();
  if (s) {
    q.set('text', s);
  }
  const bn = brandName?.trim();
  if (bn) {
    q.set('brand_name', bn);
  }
  if (brandId) {
    q.set('brand_id', brandId);
  }
  if (listStatus) {
    q.set('list_status', listStatus);
  }
  if (platform) {
    q.set('platform', platform);
  }
  const cc = country?.trim().toUpperCase();
  if (cc) {
    q.set('country', cc);
  }
  const path = `/app/v2/client/campaigns/search?${q.toString()}`;
  return get<ClientCampaignsSearchResponse>(path, token);
}

/**
 * Distinct ISO country codes for the current client team (Elasticsearch facet).
 * GET /app/v2/client/campaigns/countries
 */
export async function getClientCampaignCountries(token: string): Promise<ClientCampaignCountriesResponse> {
  return get<ClientCampaignCountriesResponse>('/app/v2/client/campaigns/countries', token);
}

/** Matches beatly-api-v2 `LITERAL_CLIENT_CALENDAR_EVENT_TYPE` */
export type ClientCalendarEventType =
  | 'CAMPAIGN_START'
  | 'CAMPAIGN_END'
  | 'BEAT_START'
  | 'BEAT_END'
  | 'DRAFT_DEADLINE'
  | 'PUBLICATION'
  | 'PUBLICATION_PUBLISHED'
  | 'SCREENSHOT_DUE';

/** Row from GET /app/v2/client/calendar/search */
export interface ClientCalendarSearchEvent {
  event_id: string;
  date_start: string;
  date: string;
  title: string;
  type: ClientCalendarEventType;
  campaign_id?: string | null;
  campaign_name?: string | null;
  brand_name?: string | null;
  beat_id?: string | null;
  beat_name?: string | null;
  collaboration_id?: string | null;
  publication_id?: string | null;
  product_id?: string | null;
  influencer_id?: string | null;
  creator_mugshot_url?: string | null;
  creator_name?: string | null;
  creator_handle?: string | null;
}

export interface ClientCalendarSearchPage {
  page_number: number;
  page_size: number;
  count_total: number;
  has_more: boolean;
  events: ClientCalendarSearchEvent[];
}

export type GetClientCalendarSearchParams = {
  pageNumber?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
  campaignId?: string;
  collaborationId?: string;
};

/**
 * Team calendar events from Elasticsearch (client ACL).
 * GET /app/v2/client/calendar/search
 */
export async function getClientCalendarSearch(
  params: GetClientCalendarSearchParams,
  token: string
): Promise<ClientCalendarSearchPage> {
  const {
    pageNumber = 0,
    pageSize = 50,
    dateFrom,
    dateTo,
    campaignId,
    collaborationId,
  } = params;
  const q = new URLSearchParams({
    page_number: String(pageNumber),
    page_size: String(pageSize),
  });
  if (dateFrom) {
    q.set('date_from', dateFrom);
  }
  if (dateTo) {
    q.set('date_to', dateTo);
  }
  if (campaignId) {
    q.set('campaign_id', campaignId);
  }
  if (collaborationId) {
    q.set('collaboration_id', collaborationId);
  }
  const path = `/app/v2/client/calendar/search?${q.toString()}`;
  return get<ClientCalendarSearchPage>(path, token);
}

// ─── Client dashboard notifications (needs attention) ────────────────────────

export type ClientNotificationActionType =
  | 'REVIEW_DRAFTS'
  | 'REVIEW_CURATION'
  | 'SUMMARY_READY'
  | 'CAMPAIGN_LAUNCHED';

export interface ClientNotificationRow {
  action_type: ClientNotificationActionType;
  campaign_id: string;
  campaign_name: string;
  count_drafts: number;
  count_creators: number;
  date_start?: string | null;
  date_sort: string;
}

export interface ClientNotificationsResponse {
  notifications: ClientNotificationRow[];
}

/**
 * Structured dashboard notifications for the signed-in client.
 * GET /app/v2/client/notifications
 *
 * `events` narrows the response to a subset of action types. Omit (or pass
 * an empty array) to receive every supported action type.
 */
export async function getClientNotifications(
  token: string,
  events?: ClientNotificationActionType[],
): Promise<ClientNotificationsResponse> {
  const qs =
    events && events.length > 0 ? `?events=${encodeURIComponent(events.join(','))}` : '';
  return get<ClientNotificationsResponse>(`/app/v2/client/notifications${qs}`, token);
}

/** Creator mugshot row from GET /app/v2/client/campaigns/creators */
export interface ClientCampaignCreatorMugshot {
  influencer_id: string;
  mugshot_url: string;
  name?: string | null;
}

export interface ClientCampaignCreatorsRow {
  campaign_id: string;
  creators: ClientCampaignCreatorMugshot[];
}

export interface ClientCampaignsCreatorsResponse {
  campaigns: ClientCampaignCreatorsRow[];
}

const CAMPAIGN_CREATORS_QUERY_CHUNK = 20;

/**
 * Batch mugshots for accepted collaborators on multiple campaigns.
 * GET /app/v2/client/campaigns/creators?campaign_ids=...
 */
export async function getCampaignsCreatorsMugshots(
  campaignIds: string[],
  token: string
): Promise<ClientCampaignsCreatorsResponse> {
  const merged: ClientCampaignCreatorsRow[] = [];
  for (let i = 0; i < campaignIds.length; i += CAMPAIGN_CREATORS_QUERY_CHUNK) {
    const chunk = campaignIds.slice(i, i + CAMPAIGN_CREATORS_QUERY_CHUNK);
    if (chunk.length === 0) continue;
    const q = new URLSearchParams({ campaign_ids: chunk.join(',') });
    const path = `/app/v2/client/campaigns/creators?${q.toString()}`;
    const r = await get<ClientCampaignsCreatorsResponse>(path, token);
    merged.push(...(r.campaigns ?? []));
  }
  return { campaigns: merged };
}

/**
 * Fetch a single campaign by id (full detail: beats, brief, total_influencer_count, etc.).
 * Same endpoint as beatly-web-app: GET /app/v2/client/campaign/:campaignId
 */
export async function getCampaign(
  campaignId: string,
  token: string,
  language = 'en'
): Promise<ClientCampaign> {
  const path = `/app/v2/client/campaign/${campaignId}?language=${language}`;
  return get<ClientCampaign>(path, token);
}

// ─── Campaign collaborations (accepted collaborators + products) ─────────────

export interface ClientCollaborationBeat {
  beat_id: string;
  beat_type?: string;
  beat_name?: string;
  platform?: string;
  [key: string]: unknown;
}

/** One reuse / republication grant (GET collaborations `content_rights` entries). */
export interface ClientContentRightRow {
  countries: string[];
  date_expire?: string | null;
  rights: string[];
}

/** Publication performance metrics (IGCP/ORGPOST); not tracker short-link clicks. */
export interface ClientCollaborationProductMetrics {
  views?: number | null;
  likes?: number | null;
  shares?: number | null;
  comments?: number | null;
  reach?: number | null;
  engagement?: number | null;
  [key: string]: unknown;
}

export interface ClientCollaborationProduct {
  product_id: string;
  product_type?: string;
  product_name?: string;
  status?: string;
  /** False for IGC beats; true for IGCP/ORGPOST — drives whether the UI shows a metrics strip. */
  has_metrics?: boolean;
  metrics?: ClientCollaborationProductMetrics | null;
  tracker_clicks?: number | null;
  preview_url?: string | null;
  permalink?: string | null;
  /** TikTok Spark ID when the beat allows exposing it to clients and the publication has one. */
  spark_id?: string | null;
  /** Instagram / Meta ad ID when the beat allows exposing it to clients and the publication has one. */
  instagram_ad_id?: string | null;
  /** Published caption when provided by api-v2 collaborations endpoint. */
  caption?: string | null;
  /** Optional compliance booleans for published/measured products. */
  compliance?: {
    brand_tagged?: boolean | null;
    hashtag_used?: boolean | null;
    product_visible?: boolean | null;
    ad_disclosure?: boolean | null;
    link_included?: boolean | null;
    [key: string]: unknown;
  } | null;
  target_url?: string | null;
  tracker_url?: string | null;
  tracker_note?: string | null;
  date_draft?: string | null;
  date_publish?: string | null;
  content_rights?: ClientContentRightRow[];
  [key: string]: unknown;
}

export interface ClientCollaborationInfluencer {
  influencer_id: string;
  mugshot_url?: string;
  name?: string | null;
  handle: string;
  platform?: string;
  permalink?: string | null;
  followers_count?: number | null;
  engagement_rate?: number | null;
  [key: string]: unknown;
}

/** Tracking links for a collaboration (GET collaborations `trackers` list). */
export interface ClientCollaborationTracker {
  target_url: string;
  tracker_url: string;
  tracker_note?: string | null;
  [key: string]: unknown;
}

/** Per-row status from GET /app/v2/client/campaign/:id/collaborations (v2ClientCampaignCollaboration). */
export type ClientCampaignCollaborationStatus =
  | 'CURATED'
  | 'CURATION_REJECTED'
  | 'CURATION_ACCEPTED'
  | 'INVITED'
  | 'INVITE_DECLINED'
  | 'ONGOING'
  | 'DONE';

export interface ClientCampaignCollaboration {
  collaboration_id: string;
  beats?: ClientCollaborationBeat[];
  influencer: ClientCollaborationInfluencer;
  status?: ClientCampaignCollaborationStatus;
  product_delivery?: string | null;
  delivery_address?: string | null;
  /** Address lines only when `product_delivery` is SHIPPING (no phone/email lines). */
  creator_shipping_address?: string | null;
  creator_phone?: string | null;
  creator_email?: string | null;
  shipping_note?: string | null;
  /** Aggregated from all products in this collaboration. */
  content_rights?: ClientContentRightRow[];
  /** Content Hub / CDN asset for this collab when per-product `preview_url` is empty (OpenAPI: collaborations[].content_hub_url). */
  content_hub_url?: string | null;
  products?: ClientCollaborationProduct[];
  platforms?: string[];
  trackers?: ClientCollaborationTracker[];
  [key: string]: unknown;
}

export interface ClientCampaignCollaborationsResponse {
  collaborations: ClientCampaignCollaboration[];
}

/** Unwrap common API envelopes so the UI always receives `{ collaborations: [...] }`. */
function normalizeCampaignCollaborationsResponse(data: unknown): ClientCampaignCollaborationsResponse {
  const empty = (): ClientCampaignCollaborationsResponse => ({ collaborations: [] });
  const asList = (v: unknown): ClientCampaignCollaboration[] | null =>
    Array.isArray(v) ? (v as ClientCampaignCollaboration[]) : null;

  if (data == null) return empty();
  const direct = asList(data);
  if (direct) return { collaborations: direct };

  if (typeof data !== 'object') return empty();
  const d = data as Record<string, unknown>;

  for (const key of [
    'collaborations',
    'accepted_collaborations',
    'campaign_collaborations',
    'rows',
    'results',
    'items',
  ]) {
    const list = asList(d[key]);
    if (list) return { collaborations: list };
  }

  for (const wrap of ['data', 'payload', 'result', 'response', 'body']) {
    const inner = d[wrap];
    const list = asList(inner);
    if (list) return { collaborations: list };
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const o = inner as Record<string, unknown>;
      for (const key of ['collaborations', 'accepted_collaborations', 'rows', 'results', 'items']) {
        const innerList = asList(o[key]);
        if (innerList) return { collaborations: innerList };
      }
    }
  }

  return empty();
}

/**
 * Collaborations for a campaign (GET /app/v2/client/campaign/:id/collaborations).
 * Omni filters the list client-side to invite-accepted rows (`ONGOING` / `DONE`) for the Collaborations tab.
 */
export async function getCampaignCollaborations(
  campaignId: string,
  token: string
): Promise<ClientCampaignCollaborationsResponse> {
  const path = `/app/v2/client/campaign/${campaignId}/collaborations`;
  const data = await get<unknown>(path, token);
  return normalizeCampaignCollaborationsResponse(data);
}

// ─── Campaign curation (suggestions / profiles) ─────────────────────────────

export interface ClientInfluencer {
  influencer_id: string;
  mugshot_url: string;
  name: string;
  handle: string;
  permalink: string;
  followers_count: number;
  engagement_rate: number;
  platform: string;
  /** From GET curation: influencer public note and/or CM `curator_note_public` (may contain HTML). */
  notes?: string | null;
  [key: string]: unknown;
}

export interface ClientCuration {
  curation_id: string;
  influencer: ClientInfluencer;
  platform: string;
  status: string;
  beats?: unknown[];
  [key: string]: unknown;
}

/**
 * Fetch curation list for a campaign (suggested creators). Same as beatly-web-app.
 * GET /app/v2/client/campaign/:campaignId/curation
 */
export async function getCampaignCuration(
  campaignId: string,
  token: string
): Promise<ClientCuration[]> {
  const path = `/app/v2/client/campaign/${campaignId}/curation`;
  const raw = await get<ClientCuration[] | { curation?: ClientCuration[] }>(path, token);
  if (Array.isArray(raw)) return raw;
  return (raw as { curation?: ClientCuration[] }).curation ?? [];
}

/** Body for accept/reject curated influencer. */
export interface CurationReasonBody {
  reason?: string;
}

/**
 * Approve a curated influencer (client curation).
 * POST /app/v2/client/curation/:curationId/accept
 */
export async function acceptCuratedInfluencer(
  curationId: string,
  token: string,
  body: CurationReasonBody = {}
): Promise<ClientCuration> {
  const path = `/app/v2/client/curation/${curationId}/accept`;
  return post<ClientCuration>(path, body, token);
}

/**
 * Reject a curated influencer (client curation).
 * POST /app/v2/client/curation/:curationId/reject
 */
export async function rejectCuratedInfluencer(
  curationId: string,
  token: string,
  body: CurationReasonBody = {}
): Promise<ClientCuration> {
  const path = `/app/v2/client/curation/${curationId}/reject`;
  return post<ClientCuration>(path, body, token);
}

// ─── Campaign drafts (content approval) ─────────────────────────────────────
// Backend GET /app/v2/client/campaign/:id/drafts returns a different shape; we map it to ClientDraftItem[].

/** One media item in a draft version (picture or video). */
export interface ClientDraftMedia {
  url: string;
  resource_type?: 'image' | 'video';
  is_raw?: boolean;
  /** Backend media id — required for structured POST approve/reject per file. */
  media_id?: string;
  /** Still / poster URL for grid thumbnails (api-v2 `preview_url`). */
  preview_url?: string;
  feedback_cm?: string;
  feedback_client?: string;
  status?: 'NOT_REVIEWED' | 'APPROVED' | 'REJECTED';
}

export interface ClientDraftCaption {
  caption?: string;
  feedback_cm?: string;
  feedback_client?: string;
  status?: 'NOT_REVIEWED' | 'APPROVED' | 'REJECTED';
}

/** One client's submitted review on a draft version (api-v2 multi-client draft review). */
export interface ClientDraftClientReview {
  user_id: string;
  name?: string | null;
  email?: string | null;
  review_status: 'APPROVED' | 'REJECTED';
  date_reviewed?: string;
  feedback?: string | null;
  feedback_topics?: string[];
}

/** Client still required to review (name/email for Omni pending rows). */
export interface ClientDraftPendingReviewer {
  user_id: string;
  name?: string | null;
  email?: string | null;
}

export interface ClientDraftVersion {
  /** API `version_id` (…-ver); required for POST approve/reject draft. */
  version_id?: string;
  version?: number;
  draft_date?: string;
  thumbnail_url?: string;
  /** All medias in this version (images and/or video) with url and resource_type for correct playback/carousel. */
  medias?: ClientDraftMedia[];
  /** Caption slots with per-field reviewer feedback (api-v2 `captions[]`). */
  captions?: ClientDraftCaption[];
  /** URL to the draft/post if available (e.g. not-yet-published post). */
  permalink?: string;
  /** Flattened first caption text for list UI (also in `captions[0]`). */
  caption?: string;
  feedback_cm?: string;
  feedback_client?: string;
  reason?: string;
  /** ISO datetime when the version was approved (client or CM). */
  date_approved?: string;
  /** ISO datetime when the version was rejected (client or CM). */
  date_rejected?: string;
  /** Display name of who approved/rejected (api-v2 `decider_name`). */
  decider_name?: string;
  status?: string;
  /**
   * Viewer's own client review row when multi-client aggregation is on and they already acted.
   * Null/absent means this viewer has not reviewed yet (overall draft may still be pending).
   */
  my_client_review?: ClientDraftClientReview | null;
  /**
   * All clients who have already reviewed this version (decision + feedback).
   * Visible to peer clients while the aggregate is still pending.
   */
  client_reviews?: ClientDraftClientReview[];
  /** Team draft-review policy from api-v2 (e.g. APPROVE_FAST_REJECT_FAST vs consensus modes). */
  client_draft_review_method?: string | null;
  /** User ids still required to review before the draft can leave pending under multi-client rules. */
  pending_client_reviewer_ids?: string[];
  /** Pending reviewers with display names for Omni “Not reviewed yet” rows. */
  pending_client_reviewers?: ClientDraftPendingReviewer[];
  /** User ids that have already submitted a client review for this version. */
  reviewed_client_reviewer_ids?: string[];
  [key: string]: unknown;
}

/** Platform for draft list icon (inferred from product_type / product_name / label). */
export type ClientDraftContentPlatform = 'instagram' | 'tiktok' | 'youtube';

/**
 * Infer the Content Approval platform icon from product type / name labels.
 * Shared by GET /drafts mapping and unsubmitted-required placeholders.
 * Unrecognized labels default to Instagram (historical default for this UI).
 */
export function inferDraftContentPlatform(
  productType?: string,
  productName?: string,
  resolvedContentLabel?: string
): ClientDraftContentPlatform {
  const blob = [productType, productName, resolvedContentLabel].filter(Boolean).join(' ').toUpperCase();
  if (blob.includes('TIKTOK') || blob.includes('TIK TOK')) return 'tiktok';
  if (blob.includes('YOUTUBE') || /\bYT\b/.test(blob)) return 'youtube';
  // Reels / Stories / Threads / Meta are treated as Instagram when the string is not TikTok or YouTube.
  if (
    blob.includes('INSTAGRAM') ||
    blob.includes('INSTA') ||
    blob.includes('REEL') ||
    blob.includes('STORY') ||
    blob.includes('THREADS') ||
    blob.includes('META') ||
    /\bIG\b/.test(blob)
  ) {
    return 'instagram';
  }
  return 'instagram';
}

export interface ClientDraftItem {
  id?: string | number;
  /** Discovery profile id for `/creators/:id` when API sends it on influencer. */
  influencer_id?: string;
  /** When set, links draft rows to GET /collaborations rows regardless of handle spelling. */
  collaboration_id?: string;
  /** Beat this draft belongs to (GET /campaign/:id/drafts `beat.beat_name`); one name per deliverable. */
  beat_name?: string;
  /** Same id space as collaboration `products[].product_id` when API provides it. */
  product_id?: string;
  draft_id?: string;
  creator_name?: string;
  handle?: string;
  avatar_url?: string;
  content_type?: string;
  /** Same as collaboration product `product_type` when API sends it on drafts. */
  product_type?: string;
  /** Inferred platform for UI icon next to content type label. */
  content_platform?: ClientDraftContentPlatform;
  publication_date?: string;
  is_video?: boolean;
  versions?: ClientDraftVersion[];
  [key: string]: unknown;
}

/** Backend response shape (api-v2 client drafts endpoint). */
interface ApiDraftMedia {
  url?: string;
  resource_type?: string;
  is_raw?: boolean;
  media_id?: string;
  preview_url?: string;
  feedback_cm?: string;
  feedback_client?: string;
  status?: 'NOT_REVIEWED' | 'APPROVED' | 'REJECTED';
  [key: string]: unknown;
}

interface ApiDraftCaption {
  caption?: string;
  feedback_cm?: string;
  feedback_client?: string;
  status?: 'NOT_REVIEWED' | 'APPROVED' | 'REJECTED';
  [key: string]: unknown;
}

interface ApiDraftVersion {
  version_id?: string;
  version_number?: number;
  date_submitted?: string;
  date_rejected?: string;
  date_approved?: string;
  decider_name?: string | null;
  medias?: ApiDraftMedia[];
  /** Optional poster / thumbnail URL from API (image); used when no image in medias. */
  thumbnail_url?: string;
  permalink?: string;
  captions?: ApiDraftCaption[] | null;
  caption?: string;
  feedback_cm?: string;
  feedback_client?: string;
  status?: 'REVIEW' | 'APPROVED' | 'REJECTED';
  /** Passthrough of api-v2 multi-client review fields (mapped onto ClientDraftVersion). */
  my_client_review?: ClientDraftVersion['my_client_review'];
  client_reviews?: ClientDraftVersion['client_reviews'];
  client_draft_review_method?: string | null;
  pending_client_reviewer_ids?: string[];
  pending_client_reviewers?: ClientDraftVersion['pending_client_reviewers'];
  reviewed_client_reviewer_ids?: string[];
  [key: string]: unknown;
}

interface ApiDraftInfluencer {
  name?: string;
  handle?: string;
  mugshot_url?: string;
  influencer_id?: string;
  [key: string]: unknown;
}

/** Nested `beat` on GET /campaign/:id/drafts items (`v2ClientLatestDraftVersion`). */
interface ApiDraftBeat {
  beat_id?: string;
  beat_name?: string;
  [key: string]: unknown;
}

interface ApiDraftItem {
  draft_id?: string;
  collaboration_id?: string;
  collaborationId?: string;
  product_id?: string;
  productId?: string;
  collaboration_product_id?: string;
  product_type?: string;
  product_name?: string;
  date_draft?: string;
  date_publish?: string;
  influencer?: ApiDraftInfluencer;
  latest_version?: ApiDraftVersion;
  versions?: ApiDraftVersion[];
  beat?: ApiDraftBeat;
  [key: string]: unknown;
}

interface ApiDraftsResponse {
  show_drafts?: boolean;
  drafts?: ApiDraftItem[];
}

function mapDraftStatus(status: string | undefined): string {
  if (status === 'APPROVED') return 'approved';
  if (status === 'REJECTED') return 'declined';
  return 'pending';
}

function trimFeedback(s: string | undefined): string | undefined {
  if (typeof s !== 'string') return undefined;
  const t = s.trim();
  return t.length > 0 ? t : undefined;
}

/**
 * Medias that have CM or client feedback text (includes raw assets when reviewers commented there).
 */
export function draftMediaWithFeedback(medias: ClientDraftMedia[] | undefined): ClientDraftMedia[] {
  if (!Array.isArray(medias) || medias.length === 0) return [];
  return medias.filter((m) => {
    const cm = trimFeedback(m.feedback_cm);
    const cl = trimFeedback(m.feedback_client);
    return Boolean(cm || cl);
  });
}

/**
 * True when any structured feedback exists for the version (general, first caption slot, or any media).
 */
export function hasDraftFeedback(version: ClientDraftVersion | undefined): boolean {
  if (!version) return false;
  if (trimFeedback(version.feedback_cm) || trimFeedback(version.feedback_client)) return true;
  const cap0 = Array.isArray(version.captions) && version.captions.length > 0 ? version.captions[0] : undefined;
  if (cap0) {
    if (trimFeedback(cap0.feedback_cm) || trimFeedback(cap0.feedback_client)) return true;
  }
  return draftMediaWithFeedback(version.medias).length > 0;
}

/** Maps Content Approval card state (camelCase UI fields) for {@link hasDraftFeedback}. */
export function hasContentApprovalCardFeedback(version: {
  feedbackCm?: string;
  feedbackClient?: string;
  captionFeedbackCm?: string;
  captionFeedbackClient?: string;
  medias?: Array<{ url: string; feedbackCm?: string; feedbackClient?: string; is_raw?: boolean }>;
}): boolean {
  return hasDraftFeedback({
    feedback_cm: version.feedbackCm,
    feedback_client: version.feedbackClient,
    captions: [{ feedback_cm: version.captionFeedbackCm, feedback_client: version.captionFeedbackClient }],
    medias: version.medias?.map((m) => ({
      url: m.url,
      is_raw: m.is_raw,
      feedback_cm: m.feedbackCm,
      feedback_client: m.feedbackClient,
    })),
  } as ClientDraftVersion);
}

/**
 * Normalize one draft version from GET /campaign/:id/drafts for Content Approval UI.
 * Also forwards multi-client review fields for pending banners and the peer Reviewers stack.
 */
function mapApiDraftVersionToClient(v: ApiDraftVersion, index: number): ClientDraftVersion {
  const apiMedias = Array.isArray(v.medias) ? v.medias : [];
  const firstMedia = apiMedias.length > 0 ? apiMedias[0] : undefined;
  const firstCaption = Array.isArray(v.captions) && v.captions.length > 0 ? v.captions[0] : undefined;
  const nonRawMedias = apiMedias.filter(m => !m.is_raw);
  const poolForThumb = nonRawMedias.length > 0 ? nonRawMedias : apiMedias;
  const firstImageInPool = poolForThumb.find(
    m => m.resource_type === 'image' && typeof m.url === 'string' && m.url.length > 0
  );
  const apiThumb = typeof v.thumbnail_url === 'string' && v.thumbnail_url.length > 0 ? v.thumbnail_url : undefined;
  const medias: ClientDraftMedia[] = apiMedias
    .filter((m): m is ApiDraftMedia & { url: string } => typeof m?.url === 'string' && m.url.length > 0)
    .map(m => {
      const st = m.status;
      const statusOk = st === 'NOT_REVIEWED' || st === 'APPROVED' || st === 'REJECTED' ? st : undefined;
      return {
        url: m.url,
        resource_type: m.resource_type === 'video' || m.resource_type === 'image' ? m.resource_type : undefined,
        is_raw: Boolean(m.is_raw),
        media_id: typeof m.media_id === 'string' && m.media_id.trim() ? m.media_id.trim() : undefined,
        preview_url: typeof m.preview_url === 'string' && m.preview_url.trim() ? m.preview_url.trim() : undefined,
        feedback_cm: trimFeedback(m.feedback_cm),
        feedback_client: trimFeedback(m.feedback_client),
        ...(statusOk ? { status: statusOk } : {}),
      };
    });
  const captions: ClientDraftCaption[] | undefined = (() => {
    const raw = v.captions;
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const out: ClientDraftCaption[] = [];
    for (const c of raw) {
      if (!c || typeof c !== 'object') continue;
      const st = c.status;
      const statusOk = st === 'NOT_REVIEWED' || st === 'APPROVED' || st === 'REJECTED' ? st : undefined;
      out.push({
        caption: typeof c.caption === 'string' ? c.caption : undefined,
        feedback_cm: trimFeedback(c.feedback_cm),
        feedback_client: trimFeedback(c.feedback_client),
        ...(statusOk ? { status: statusOk } : {}),
      });
    }
    return out.length > 0 ? out : undefined;
  })();
  const feedbackCm = trimFeedback(v.feedback_cm);
  const feedbackClient = trimFeedback(v.feedback_client);
  const dateApproved = typeof v.date_approved === 'string' && v.date_approved.trim() ? v.date_approved.trim() : undefined;
  const dateRejected = typeof v.date_rejected === 'string' && v.date_rejected.trim() ? v.date_rejected.trim() : undefined;
  const deciderName =
    typeof v.decider_name === 'string' && v.decider_name.trim() ? v.decider_name.trim() : undefined;
  return {
    version_id: typeof v.version_id === 'string' && v.version_id.trim() ? v.version_id.trim() : undefined,
    version: typeof v.version_number === 'number' ? v.version_number : index + 1,
    draft_date: typeof v.date_submitted === 'string' ? v.date_submitted : undefined,
    thumbnail_url: firstImageInPool?.url ?? apiThumb ?? firstMedia?.url,
    medias: medias.length > 0 ? medias : undefined,
    captions,
    permalink: typeof v.permalink === 'string' && v.permalink.length > 0 ? v.permalink : undefined,
    caption: firstCaption?.caption ?? v.caption,
    ...(feedbackCm ? { feedback_cm: feedbackCm } : {}),
    ...(feedbackClient ? { feedback_client: feedbackClient } : {}),
    ...(dateApproved ? { date_approved: dateApproved } : {}),
    ...(dateRejected ? { date_rejected: dateRejected } : {}),
    ...(deciderName ? { decider_name: deciderName } : {}),
    status: mapDraftStatus(v.status),
    // Multi-client draft review: peer rows + pending names so Omni can show who acted / who is left.
    ...(v.my_client_review && typeof v.my_client_review === 'object'
      ? { my_client_review: v.my_client_review }
      : {}),
    ...(Array.isArray(v.client_reviews) ? { client_reviews: v.client_reviews } : {}),
    ...(typeof v.client_draft_review_method === 'string' && v.client_draft_review_method.trim()
      ? { client_draft_review_method: v.client_draft_review_method.trim() }
      : {}),
    ...(Array.isArray(v.pending_client_reviewer_ids)
      ? {
          pending_client_reviewer_ids: v.pending_client_reviewer_ids.filter(
            (id): id is string => typeof id === 'string' && id.trim().length > 0,
          ),
        }
      : {}),
    ...(Array.isArray(v.pending_client_reviewers)
      ? { pending_client_reviewers: v.pending_client_reviewers }
      : {}),
    ...(Array.isArray(v.reviewed_client_reviewer_ids)
      ? {
          reviewed_client_reviewer_ids: v.reviewed_client_reviewer_ids.filter(
            (id): id is string => typeof id === 'string' && id.trim().length > 0,
          ),
        }
      : {}),
  };
}

function readDraftProductId(item: ApiDraftItem): string | undefined {
  const r = item as Record<string, unknown>;
  for (const k of ['product_id', 'productId', 'collaboration_product_id', 'collaborationProductId']) {
    const v = r[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

/** `c35a4e40-0001-548890c9-001-01-ver` → `c35a4e40-0001-548890c9-001-pro` (matches collaborations `product_id`). */
function productIdFromDraftId(draftId: string | undefined): string | undefined {
  if (!draftId || typeof draftId !== 'string') return undefined;
  const t = draftId.trim();
  if (!t.toLowerCase().endsWith('-ver')) return undefined;
  if (!/-\d+-ver$/i.test(t)) return undefined;
  return t.replace(/-\d+-ver$/i, '-pro');
}

function readDraftCollaborationId(item: ApiDraftItem): string | undefined {
  if (typeof item.collaboration_id === 'string' && item.collaboration_id.trim()) return item.collaboration_id.trim();
  if (typeof item.collaborationId === 'string' && item.collaborationId.trim()) return item.collaborationId.trim();
  const r = item as Record<string, unknown>;
  const v = r.campaign_collaboration_id ?? r.campaignCollaborationId;
  if (typeof v === 'string' && v.trim()) return v.trim();
  return undefined;
}

/** Normalize `beat.beat_name` from the client drafts API; exported for tests. */
export function readDraftBeatName(item: { beat?: { beat_name?: unknown } | null }): string | undefined {
  const beat = item.beat;
  if (!beat || typeof beat !== 'object') return undefined;
  const raw = typeof beat.beat_name === 'string' ? beat.beat_name.trim() : '';
  return raw.length > 0 ? raw : undefined;
}

function mapApiDraftItemToClient(item: ApiDraftItem, index: number): ClientDraftItem {
  const versions = item.versions ?? (item.latest_version ? [item.latest_version] : []);
  const inf = item.influencer;
  const isVideo =
    item.product_type?.toUpperCase().includes('VIDEO') ||
    item.product_type?.toUpperCase().includes('REEL') ||
    false;
  const contentTypeLabel = item.product_name ?? item.product_type ?? 'Content';
  const collaboration_id = readDraftCollaborationId(item);
  const product_id =
    readDraftProductId(item) ??
    productIdFromDraftId(typeof item.draft_id === 'string' ? item.draft_id : undefined);
  const influencer_id =
    typeof inf?.influencer_id === 'string' && inf.influencer_id.trim() ? inf.influencer_id.trim() : undefined;
  const beat_name = readDraftBeatName(item);
  return {
    id: index + 1,
    draft_id: typeof item.draft_id === 'string' && item.draft_id.trim() ? item.draft_id.trim() : undefined,
    ...(influencer_id ? { influencer_id } : {}),
    ...(collaboration_id ? { collaboration_id } : {}),
    ...(beat_name ? { beat_name } : {}),
    ...(product_id ? { product_id } : {}),
    ...(typeof item.product_type === 'string' && item.product_type.trim()
      ? { product_type: item.product_type.trim() }
      : {}),
    creator_name: inf?.name,
    handle: inf?.handle,
    avatar_url: inf?.mugshot_url,
    content_type: contentTypeLabel,
    content_platform: inferDraftContentPlatform(item.product_type, item.product_name, contentTypeLabel),
    publication_date: typeof item.date_publish === 'string' ? item.date_publish : undefined,
    is_video: isVideo,
    versions: versions.map(mapApiDraftVersionToClient),
  };
}

/**
 * Fetch drafts (content approval) for a campaign.
 * GET /app/v2/client/campaign/:campaignId/drafts
 * Maps backend response to ClientDraftItem[] for the UI.
 */
export async function getCampaignDrafts(
  campaignId: string,
  token: string
): Promise<ClientDraftItem[]> {
  const path = `/app/v2/client/campaign/${campaignId}/drafts`;
  const raw = await get<ApiDraftsResponse>(path, token);
  const drafts = raw?.drafts ?? [];
  return drafts.map(mapApiDraftItemToClient);
}

/**
 * Body for POST /app/v2/client/draft/:id/approve|reject (api-v2 `v2ClientDraftReview`).
 * Use structured captions/medias when possible; `feedback` is general notes (topics, extra comments, orphans).
 */
export interface ClientDraftReviewBody {
  feedback: string;
  captions?: Array<{ feedback?: string | null; action?: 'APPROVE' | 'REJECT' | null }>;
  medias?: Array<{
    media_id: string;
    feedback?: string | null;
    action?: 'APPROVE' | 'REJECT' | null;
  }>;
}

/**
 * Approve a draft version (client). `draftVersionId` is the …-ver id (same as item `draft_id` from GET drafts).
 * POST /app/v2/client/draft/{draftVersionId}/approve
 */
export async function postClientDraftApprove(
  draftVersionId: string,
  token: string,
  body: ClientDraftReviewBody
): Promise<unknown> {
  const path = `/app/v2/client/draft/${encodeURIComponent(draftVersionId)}/approve`;
  return post(path, body, token);
}

/**
 * Reject a draft version (client).
 * POST /app/v2/client/draft/{draftVersionId}/reject
 */
export async function postClientDraftReject(
  draftVersionId: string,
  token: string,
  body: ClientDraftReviewBody
): Promise<unknown> {
  const path = `/app/v2/client/draft/${encodeURIComponent(draftVersionId)}/reject`;
  return post(path, body, token);
}

// ---------------------------------------------------------------------------
// Team ad accounts (Meta/TikTok OAuth) — GET/POST/DELETE /app/v2/team/:teamId/ad-accounts/*
// ---------------------------------------------------------------------------

export type AdAccountSource = 'meta' | 'tiktok';

export interface TeamAdAccountRow {
  source: AdAccountSource;
  account_id: string;
  account_name?: string | null;
  currency?: string | null;
  timezone?: string | null;
  connected_by_email?: string | null;
  date_created?: string | null;
}

export interface TeamAdAccountListResponse {
  accounts: TeamAdAccountRow[];
}

export interface TeamAdAccountAuthorizeUrlResponse {
  url: string;
}

export interface TeamPendingAdAccount {
  account_id: string;
  account_name?: string | null;
  currency?: string | null;
  timezone?: string | null;
}

export interface TeamPendingAdAccountSessionResponse {
  session_id: string;
  source: AdAccountSource;
  accounts: TeamPendingAdAccount[];
}

export interface TeamAdAccountConnectRequest {
  session_id: string;
  account_ids: string[];
}

function teamAdAccountsPath(teamId: string, suffix = ''): string {
  return `/app/v2/team/${encodeURIComponent(teamId)}/ad-accounts${suffix}`;
}

/**
 * List connected Meta/TikTok ad accounts for a team.
 * GET /app/v2/team/:teamId/ad-accounts
 */
export async function listTeamAdAccounts(
  teamId: string,
  token: string,
): Promise<TeamAdAccountListResponse> {
  return get<TeamAdAccountListResponse>(teamAdAccountsPath(teamId), token);
}

/**
 * Start Meta OAuth — returns provider authorize URL (full-page redirect).
 * GET /app/v2/team/:teamId/ad-accounts/oauth/meta/authorize-url
 */
export async function getTeamMetaAuthorizeUrl(
  teamId: string,
  token: string,
): Promise<TeamAdAccountAuthorizeUrlResponse> {
  return get<TeamAdAccountAuthorizeUrlResponse>(
    teamAdAccountsPath(teamId, '/oauth/meta/authorize-url'),
    token,
  );
}

/**
 * Start TikTok OAuth — returns provider authorize URL (full-page redirect).
 * GET /app/v2/team/:teamId/ad-accounts/oauth/tiktok/authorize-url
 */
export async function getTeamTiktokAuthorizeUrl(
  teamId: string,
  token: string,
): Promise<TeamAdAccountAuthorizeUrlResponse> {
  return get<TeamAdAccountAuthorizeUrlResponse>(
    teamAdAccountsPath(teamId, '/oauth/tiktok/authorize-url'),
    token,
  );
}

/**
 * Resolve a pending OAuth session after provider redirect (?session= query param).
 * GET /app/v2/team/:teamId/ad-accounts/oauth/pending/:sessionId
 */
export async function getTeamAdAccountsPendingSession(
  teamId: string,
  sessionId: string,
  token: string,
): Promise<TeamPendingAdAccountSessionResponse> {
  return get<TeamPendingAdAccountSessionResponse>(
    teamAdAccountsPath(teamId, `/oauth/pending/${encodeURIComponent(sessionId)}`),
    token,
  );
}

/**
 * Persist selected ad accounts from a pending OAuth session.
 * POST /app/v2/team/:teamId/ad-accounts/connect
 */
export async function connectTeamAdAccounts(
  teamId: string,
  token: string,
  body: TeamAdAccountConnectRequest,
): Promise<TeamAdAccountListResponse> {
  return post<TeamAdAccountListResponse>(teamAdAccountsPath(teamId, '/connect'), body, token);
}

/**
 * Disconnect one ad account for a team (team admin only).
 * DELETE /app/v2/team/:teamId/ad-accounts/:source/:accountId
 */
export async function deleteTeamAdAccount(
  teamId: string,
  source: AdAccountSource,
  accountId: string,
  token: string,
): Promise<{ ok: boolean }> {
  const path = teamAdAccountsPath(teamId, `/${encodeURIComponent(source)}/${encodeURIComponent(accountId)}`);
  return del<{ ok: boolean }>(path, token);
}

/** Effective curation/draft reviewer row for a campaign (team default + override). */
export interface CampaignReviewerRow {
  user_id: string;
  name?: string | null;
  email?: string | null;
  is_curation_reviewer: boolean;
  is_draft_reviewer: boolean;
  team_is_curation_reviewer: boolean;
  team_is_draft_reviewer: boolean;
}

export interface CampaignReviewersResponse {
  reviewers: CampaignReviewerRow[];
}

export interface CampaignReviewerUpdateBody {
  reviewers: Array<{
    user_id: string;
    is_curation_reviewer?: boolean;
    is_draft_reviewer?: boolean;
  }>;
}

/**
 * List effective client reviewers for a campaign.
 * GET /app/v2/client/campaign/:campaignId/reviewers
 */
export async function getCampaignReviewers(
  campaignId: string,
  token: string,
): Promise<CampaignReviewersResponse> {
  const path = `/app/v2/client/campaign/${encodeURIComponent(campaignId)}/reviewers`;
  return get<CampaignReviewersResponse>(path, token);
}

/**
 * Override effective reviewer flags for a campaign (admin only).
 * PATCH /app/v2/client/campaign/:campaignId/reviewers/update
 */
export async function updateCampaignReviewers(
  campaignId: string,
  token: string,
  body: CampaignReviewerUpdateBody,
): Promise<CampaignReviewersResponse> {
  const path = `/app/v2/client/campaign/${encodeURIComponent(campaignId)}/reviewers/update`;
  return patch<CampaignReviewersResponse>(path, body, token);
}
